import { Cursor, ArrowsOutCardinal, ArrowClockwise, CornersOut, Hand, PencilSimple } from '@phosphor-icons/react';
import { useToolStore, type ToolMode, type BoneMode } from '../../stores/toolStore';
import { Tooltip } from '../ui/tooltip';

const TOOLS: { mode: ToolMode; icon: React.ReactNode; title: string }[] = [
  { mode: 'pan',    icon: <Hand size={14} />,              title: 'Pan (Q)' },
  { mode: 'select', icon: <Cursor size={14} />,            title: 'Select (W)' },
  { mode: 'move',   icon: <ArrowsOutCardinal size={14} />, title: 'Move (E)' },
  { mode: 'rotate', icon: <ArrowClockwise size={14} />,    title: 'Rotate (R)' },
  { mode: 'scale',  icon: <CornersOut size={14} />,        title: 'Scale (T)' },
  { mode: 'draw',   icon: <PencilSimple size={14} />,      title: 'Draw' },
];

const BONE_MODE_LABELS: Record<BoneMode, string> = {
  off:  'Bones',
  on:   'Bones',
  only: 'Bones',
};

const BONE_MODE_SUBTITLES: Record<BoneMode, string> = {
  off:  'off',
  on:   'on',
  only: 'only',
};

const BONE_MODE_TITLES: Record<BoneMode, string> = {
  off:  'Bones off — click to show bones (B)',
  on:   'Spine + Bones — click for bones only (B)',
  only: 'Bones only — click to hide bones (B)',
};

export default function ToolSwitcher() {
  const { activeTool, setTool, boneMode, cycleBoneMode } = useToolStore();

  return (
    <div className="flex items-center gap-1 shrink-0">
      {TOOLS.map(({ mode, icon, title }) => (
        <Tooltip key={mode} content={title}>
          <button
            onClick={() => setTool(mode)}
            data-testid={`tool-${mode}`}
            aria-pressed={activeTool === mode}
            className={`w-[28px] h-[28px] flex items-center justify-center rounded transition-all duration-fast ${
              activeTool === mode
                ? 'bg-accent-muted text-accent border border-accent/30'
                : 'bg-bg-input border border-border text-text-secondary hover:text-text-primary hover:bg-bg-hover'
            }`}
          >
            {icon}
          </button>
        </Tooltip>
      ))}

      <div className="w-px h-5 bg-border mx-0.5" />

      <Tooltip content={BONE_MODE_TITLES[boneMode]}>
        <button
          onClick={cycleBoneMode}
          data-testid="tool-bones"
          aria-label={BONE_MODE_TITLES[boneMode]}
          className={`px-2 h-[28px] flex items-center gap-1 justify-center rounded text-xs font-mono transition-all duration-fast ${
            boneMode === 'off'
              ? 'bg-bg-input border border-border text-text-secondary hover:text-text-primary hover:bg-bg-hover'
              : 'bg-accent-muted text-accent border border-accent/30'
          }`}
        >
          {BONE_MODE_LABELS[boneMode]}
          <span className={`text-[9px] font-sans leading-none px-1 py-0.5 rounded ${
            boneMode === 'off'  ? 'hidden' :
            boneMode === 'on'   ? 'bg-accent/20 text-accent' :
                                  'bg-accent text-white'
          }`}>
            {boneMode !== 'off' && BONE_MODE_SUBTITLES[boneMode]}
          </span>
        </button>
      </Tooltip>
    </div>
  );
}
